"use client";

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";

function SkeletonBox({ className }) {
  return (
    <div
      className={`bg-gray-700/50 animate-pulse rounded-lg ${className}`}
    ></div>
  );
}

function SkeletonText({ className }) {
  return (
    <div className={`bg-gray-600/50 animate-pulse rounded ${className}`}></div>
  );
}

export default function FAQPage() {
  const new_backend_domain_name = `https://bi.oasisyangonmm.com`;
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${new_backend_domain_name}/api/faqs`);
      if (response.status == 200) {
        console.log(response.data);
        setFaqs(response.data.data);
      } else {
        console.log("error");
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error in fetchApi:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-dark-primary font-lora">
        {/* Hero Skeleton */}
        <section className="relative w-full h-[40vh] flex items-center justify-center overflow-hidden">
          <SkeletonBox className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
          <div className="absolute inset-0 bg-dark-blue/80"></div>
          <div className="absolute z-10 max-w-6xl mx-auto text-center px-4 md:px-6 space-y-6">
            <div className="space-y-4">
              <SkeletonText className="h-12 md:h-16 w-3/4 mx-auto" />
              <SkeletonText className="h-6 md:h-8 w-1/2 mx-auto" />
            </div>
          </div>
        </section>

        {/* FAQ Skeleton */}
        <section className="w-full py-12 sm:py-16 md:py-20 px-4 md:px-6 bg-gray-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 md:mb-16 space-y-6">
              <SkeletonText className="h-12 w-2/3 mx-auto" />
              <SkeletonText className="h-6 w-3/4 mx-auto" />
            </div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-black/40 rounded-2xl p-6 space-y-3">
                  <SkeletonText className="h-6 w-3/4" />
                  <SkeletonText className="h-4 w-full" />
                  <SkeletonText className="h-4 w-5/6" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-dark-primary font-lora">
     <section className="relative w-full min-h-[40vh] lg:min-h-[50vh] flex items-center justify-center overflow-hidden bg-dark-blue pt-24 pb-8 lg:pt-28">
  <div className="relative z-10 max-w-5xl mx-auto text-center px-4 md:px-6 space-y-4 md:space-y-6">
    {/* Badge */}
    <div className="inline-block px-4 py-1.5 bg-brand-red/20 rounded-full border border-brand-red/40">
      <span className="text-brand-beige font-manrope font-semibold text-xs uppercase tracking-widest">
        FAQ
      </span>
    </div>

    {/* Heading */}
    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-lora font-bold leading-tight">
      <span className="text-brand-beige">Frequently Asked</span>
      <span className="text-brand-red"> Questions</span>
    </h1>

    {/* Decorative line */}
    <div className="w-20 h-1 bg-brand-red mx-auto rounded-full"></div>

    {/* Description */}
    <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-manrope">
      Find answers to common questions about our programs, admissions, and student life at Bridge International
    </p>

    {/* CTA Button */}
    <div className="pt-4">
      <Link
        to="/enquire"
        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red hover:bg-red-700 text-white font-manrope font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-brand-red/20"
      >
        Contact Us
        <FiArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
</section>

      {/* FAQ Section */}
      <section className="w-full py-16 md:py-24 px-4 md:px-6 bg-gray-900 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-brand-red/10 rounded-full blur-xl"></div>
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-brand-beige/10 rounded-full blur-xl"></div>

        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16 space-y-6">
            <h2 className="text-3xl md:text-4xl font-lora font-bold text-brand-beige leading-tight">
              Everything You Need to Know
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Get answers to the most frequently asked questions about our programs, admissions process, and student experience
            </p>
            <div className="w-20 h-1 bg-brand-red mx-auto rounded-full"></div>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                className="bg-black/40 backdrop-blur-lg rounded-2xl border border-white/10 overflow-hidden transition-all duration-500 hover:border-brand-beige/30 hover:shadow-lg hover:shadow-brand-red/10 group"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 md:px-8 py-6 text-left flex items-center justify-between hover:bg-white/5 transition-all duration-300 group-hover:bg-white/5"
                  aria-expanded={openFaq === index}
                  aria-controls={`faq-content-${index}`}
                >
                  <div className="flex items-start space-x-4">
                    <div
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-1 ${
                        openFaq === index ? "bg-brand-red" : "bg-brand-beige/20"
                      }`}
                    >
                      <span
                        className={`text-xs font-bold ${
                          openFaq === index ? "text-white" : "text-brand-beige"
                        }`}
                      >
                        {index + 1}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-lora font-semibold text-brand-beige pr-4 text-left transition-colors duration-300 group-hover:text-brand-red">
                      {faq.q}
                    </h3>
                  </div>
                  <div
                    className={`flex-shrink-0 w-7 h-7 flex items-center justify-center transition-all duration-300 rounded-full bg-black/30 group-hover:bg-brand-red/20 ${
                      openFaq === index ? "bg-brand-red/20" : ""
                    }`}
                  >
                    <svg
                      className={`w-5 h-5 transition-all duration-300 ${
                        openFaq === index
                          ? "text-brand-red rotate-180 scale-110"
                          : "text-brand-beige"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>

                <div
                  id={`faq-content-${index}`}
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    openFaq === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 md:px-8 pb-6 md:pb-8 pl-14">
                    <div className="w-12 h-0.5 bg-gradient-to-r from-brand-red to-brand-beige mb-4"></div>
                    <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center mt-12 md:mt-16 pt-8 border-t border-white/10">
            <h3 className="text-xl font-lora font-semibold text-brand-beige mb-4">
              Still have questions?
            </h3>
            <p className="text-gray-300 mb-6">
              Our admissions team is here to help you with any additional questions you may have.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/enquire")}
                className="px-6 py-3 bg-brand-red hover:bg-brand-red/90 text-white font-semibold rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-opacity-50"
              >
                Contact Admissions
              </button>
              <Link
                to="/admissions"
                className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-brand-beige font-semibold rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 border border-gray-700"
              >
                View Admissions
              </Link>
            </div>

            {/* Social Media Links */}
            <div className="grid grid-cols-4 gap-6 max-w-sm mx-auto pt-12 items-center justify-center">
              <a 
                href="https://www.facebook.com/profile.php?id=61590266777397" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group aspect-square bg-white/[0.02] border border-white/5 hover:border-[#1877F2]/40 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:bg-[#1877F2]/5 p-3"
              >
                <svg className="w-8 h-8 text-[#1877F2] transition-all duration-300 group-hover:drop-shadow-[0_0_8px_rgba(24,119,242,0.6)]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.75z" />
                </svg>
              </a>

              <a 
                href="https://www.tiktok.com/@bridgeinternational_th" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group aspect-square bg-white/[0.02] border border-white/5 hover:border-white/20 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:bg-black/40 p-3"
              >
                <svg className="w-7 h-7 text-white transition-all duration-300 group-hover:drop-shadow-[1.5px_1.5px_0px_#FE2C55] drop-shadow-[-1.5px_-1.5px_0px_#25F4EE]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.06-2.89-.5-4.09-1.31-.15-.1-.29-.21-.45-.33v5.93c.01 2-.54 4.05-1.8 5.61-1.4 1.72-3.66 2.77-5.9 2.64-2.8-.16-5.46-2.24-6.01-5.01-.64-3.23 1.25-6.7 4.4-7.46.85-.2 1.73-.2 2.58-.04v4.06c-.73-.21-1.55-.13-2.2.27-.9.56-1.31 1.67-1.01 2.68.35 1.17 1.6 1.94 2.8 1.68 1.1-.24 1.87-1.28 1.87-2.4V.02z"/>
                </svg>
              </a>

              <a 
                href="https://www.youtube.com/@BridgeInternationalbangkok" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group aspect-square bg-white/[0.02] border border-white/5 hover:border-[#FF0000]/40 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:bg-[#FF0000]/5 p-3"
              >
                <svg className="w-8 h-8 text-[#FF0000] transition-all duration-300 group-hover:drop-shadow-[0_0_8px_rgba(255,0,0,0.6)]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>

              <a 
                href="https://www.instagram.com/bridgeintercollegeth/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group aspect-square bg-white/[0.02] border border-white/5 hover:border-[#E1306C]/40 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:bg-[#E1306C]/5 p-2.5"
              >
                <svg className="w-9 h-9 transition-all duration-300 group-hover:drop-shadow-[0_0_10px_rgba(225,48,108,0.7)]" style={{ color: '#E1306C' }} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <rect x="2" y="2" width="20" h="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full relative bg-cover bg-center bg-no-repeat py-20 md:py-28 flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-brand-red/30 to-black/90"></div>
        
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-lora font-bold text-white leading-tight">
              Ready to Start Your <span className="text-brand-red">Journey?</span>
            </h2>
            <p className="text-lg md:text-xl text-brand-beige max-w-2xl mx-auto leading-relaxed font-manrope">
              Join Bridge International and take the first step towards your dream university education.
            </p>
            <div className="pt-4">
              <Link
                to="/enquire"
                className="inline-flex items-center px-8 md:px-12 py-4 md:py-6 bg-brand-red hover:bg-brand-red/90 text-white font-bold text-lg md:text-xl shadow-xl hover:shadow-brand-red/50 transition-all duration-300 hover:scale-105 rounded-lg"
              >
                APPLY NOW
                <svg
                  className="w-5 h-5 md:w-6 md:h-6 ml-3 group-hover:translate-x-2 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}